import { getApprovedVisitFinancialContext } from '../../utils/visitQuickPrint.js';
import { customerBillingItems, resolveBillingDocument, billingError, money } from '../../utils/billingDocument.js';
const isUuidLike = (value = '') => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim());
const humanDocNumber = (...values) => {
    const found = values.map((value) => String(value || '').trim()).find((value) => value && !isUuidLike(value));
    return found || '';
};
export const extractVisitItems = (visit) => (Array.isArray(visit?.items) ? visit.items : []);
export const customerPrintableItems = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.customerBillable !== false && item?.customer_billable !== false) : []);
export const normalizePrintItem = (item, fallbackName = 'بند') => {
    const normalized = customerBillingItems([{ ...item, description: item?.description || item?.name || fallbackName }])[0];
    if (!normalized) throw billingError('DOCUMENT_ITEM_NOT_BILLABLE');
    const { quantity, price } = normalized;
    const itemName = item?.name || item?.description || item?.serviceName || fallbackName;
    return {
        ...normalized,
        name: itemName,
        description: itemName,
        itemType: item?.itemType || item?.type || 'service',
        billingType: item?.billingType || item?.billing_type || 'workshop',
        quantity,
        qty: quantity,
        price,
        unit_price: price,
        total: normalized.total,
        unit: item?.unit || 'حبة',
    };
};
export const buildVisitPayload = async ({ docType, visit, vehicle }) => {
    const labelMap = {
        invoice: 'فاتورة',
        diagnosis: 'تقرير تشخيص',
        quote: 'عرض سعر',
        receipt: 'سند زيارة',
    };
    if (!visit?.id || !Array.isArray(visit.items)) throw billingError('DOCUMENT_ITEMS_UNAVAILABLE');
    const visitItems = extractVisitItems(visit);
    const workshopOnlyItems = customerPrintableItems(visitItems);
    const items = workshopOnlyItems.map((item) => normalizePrintItem(item));
    const plainNotes = typeof visit?.notes === 'string' ? visit.notes : (visit?.notesText || '');
    const visitDate = String(visit?.entryDate || visit?.entry_date || visit?.created_at || visit?.createdAt || '').slice(0, 10);
    const deliveryDate = String(visit?.exitDate || visit?.delivery_date || visit?.delivered_at || visit?.completed_at || '').slice(0, 10);
    const printableItems = items;
    const visitFinalization = getApprovedVisitFinancialContext(visit);
    const approvedFinalTotal = docType === 'invoice' ? visitFinalization.final_customer_total : null;
    if (docType === 'invoice' && (visitFinalization.financial_state !== 'finalized' || !Number.isFinite(approvedFinalTotal) || approvedFinalTotal <= 0)) {
        throw new Error('visit_final_customer_total_required_for_quickprint');
    }
    const confirmedPaid = visitFinalization.confirmed_paid;
    const invoice = visit.invoice || null;
    if (docType === 'invoice' && (visit.invoiceReadStatus === 'unavailable' || !invoice?.id))
        throw billingError('ISSUED_INVOICE_REQUIRED', 'بيانات الفاتورة الصادرة غير متاحة؛ أعد تحميل الزيارة.');
    if (docType === 'invoice' && money(invoice.total) !== approvedFinalTotal)
        throw billingError('DOCUMENT_TOTAL_MISMATCH');
    const documentId = docType === 'invoice' ? invoice.id : `${visit.id}:${docType}`;
    const documentNumber = docType === 'invoice' ? invoice.invoice_number : `زيارة ${visit.visitNumberDisplay || visit.visitNumber || ''} — مسودة`;
    const payload = {
        doc_type: docType,
        document_id: documentId,
        document_version: docType === 'invoice' ? 1 : Number(visit.revision ?? 0),
        visit_revision: Number(visit.revision ?? 0),
        visit_id: visit?.id,
        final_customer_total: approvedFinalTotal,
        approved_final_customer_total: approvedFinalTotal,
        items: printableItems,
        customer: {
            id: vehicle?.customerId || vehicle?.customer_id || null,
            name: vehicle?.customerName || vehicle?.ownerName || '',
            phone: vehicle?.customerPhone || vehicle?.ownerPhone || '',
        },
        vehicle: {
            id: vehicle?.id,
            plate: vehicle?.plateNumber || vehicle?.plate || '',
            model: vehicle?.vehicleModel || vehicle?.model || '',
            brand: vehicle?.vehicleBrand || vehicle?.brand || '',
            year: vehicle?.year || vehicle?.vehicleYear || '',
            vin: vehicle?.vin || vehicle?.chassisNumber || '',
            mileage: vehicle?.mileage || visit?.mileage || '',
            notes: plainNotes,
        },
        settings: {
            document_id: documentId,
            document_state: docType === 'invoice' ? invoice.status : 'draft',
            visit_id: visit?.id,
            final_customer_total: approvedFinalTotal,
            total_source: Number.isFinite(approvedFinalTotal) ? 'approved_visit_final_customer_total' : 'visit_items_preview',
            totals: {
                total: approvedFinalTotal,
                paid: docType === 'invoice' ? confirmedPaid : 0,
            },
            document_number: documentNumber,
            document_title: `${labelMap[docType] || 'مستند'}${docType === 'invoice' ? '' : ' — مسودة'}`,
            date: docType === 'invoice' ? String(invoice.issued_at || '').slice(0, 10) : visitDate,
            entry_date: visitDate,
            delivery_date: deliveryDate,
            job_order: humanDocNumber(visit?.jobOrder, visit?.job_order, visit?.workOrderNumber, visit?.work_order_number),
            payment_method: visit?.paymentMethod || visit?.payment_method || '',
            description: docType === 'diagnosis' ? 'تقرير تشخيص للمركبة' : 'خدمات صيانة وإصلاح',
            notes: plainNotes,
            complaint: visit?.complaint || visit?.customer_complaint || visit?.issue || '',
            inspection: visit?.inspection || visit?.diagnosis || visit?.diagnosis_result || '',
            dtc: visit?.dtc || visit?.dtc_codes || '',
            recommendation: visit?.recommendation || visit?.recommendations || '',
            warranty: visit?.warranty || '',
            technician: visit?.technician || visit?.technicianName || visit?.technician_name || '',
        },
    };
    const resolved = resolveBillingDocument(payload);
    payload.settings.status = resolved.status;
    payload.settings.totals = resolved.totals;
    return payload;
};
